import { InjectionToken } from '@angular/core';
import { environment } from '../../environments/environment';

export interface PaymentConfig {
  /** Master switch for the whole payment step. Off = today's behavior, byte for byte. */
  acceptPayments: boolean;
  /** Publishable key — public by design; the secret key never reaches the client. */
  stripePublishableKey: string;
  /**
   * The bootstrap snapshot of Stripe's `?session_id=…`, carried here so the return path is
   * drivable from a spec — a module-level constant is not. Optional: an absent field means
   * "no return in progress", the same as the snapshot's own `null`.
   */
  returnedSessionId?: string | null;
}

/**
 * The flag is injected rather than read off `environment` directly so a spec can drive
 * both states. Unit tests only ever load `environment.test.ts`, which pins the flag off,
 * so a component reading it directly would make the flag-on path untestable.
 */
/**
 * Stripe returns the user to `<origin>/?session_id=…#/partner` — query *before* the fragment,
 * because the router cannot see a query that lands after the `#`. Verified against a real
 * redirect: the browser does land on that URL. But `withHashLocation()` rewrites the address
 * to `#/partner` on the router's first navigation, and the query is already gone by the time
 * any component's `ngOnInit` runs — so it is read once at module load and kept. `main.ts`
 * imports this module before `bootstrapApplication` to guarantee that ordering.
 */
const returnedSessionId = new URLSearchParams(window.location.search).get('session_id');

/** The Checkout Session id Stripe appended when returning from an off-site authentication. */
export function checkoutSessionIdFromReturnUrl(): string | null {
  return returnedSessionId;
}

export const PAYMENT_CONFIG = new InjectionToken<PaymentConfig>('PAYMENT_CONFIG', {
  providedIn: 'root',
  factory: () => ({
    acceptPayments: environment.acceptPayments,
    stripePublishableKey: environment.stripePublishableKey,
    returnedSessionId: checkoutSessionIdFromReturnUrl(),
  }),
});
